import React, { useCallback, useEffect, useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faExclamationTriangle, 
  faInfoCircle, 
  faCheckCircle, 
  faFileInvoice,
  faUser,
  faUniversity,
  faCalculator,
  faEye,
  faLightbulb,
  faArrowLeft,
  faEdit,
  faPrint
} from "@fortawesome/free-solid-svg-icons";

import {
  Card,
  Button,
  Form,
  Modal,
  Container,
  Row,
  Spinner,
  Alert,
  Col,
  Table
} from "@themesberg/react-bootstrap";
import Attachment from "../../../components/Attachment";
import { toast } from "react-hot-toast";
import { useDropzone } from "react-dropzone";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import useQuery from "../../../hooks/useQuery";
import { apiURL, fetchApi, postApi } from "../../../redux/api";
import { Routes } from "../../../routes";
import BackButton from "../../../components/BackButton";
import CustomForm from "../../../components/CustomForm";
import { formatNumber } from "../../../redux/utils";
import CustomButton from "../../../components/CustomButton";
import NewContractorForm from "./NewContractorForm";
import TaxItem from "../contractor-schedule/TaxItem";
import CapitalProjectTaxSection from "./CapitalProjectTaxSection";
import { useSelector } from "react-redux";
import NewPVBankDetails from "./NewPVBankDetails";
import Trailbalance from "./HtmlPvPdf";
import { useResponsive } from "../../../hooks/useResponsive";
import ResponsiveContainer from "../../../components/ResponsiveContainer";
import ResponsiveButton from "../../../components/ResponsiveButton";
import "./preview-styles.css";

export const imageExtensions = [".jpg", ".jpeg", ".png", ".gif"];

export const paymentTypes = ["Direct Payment", "Power of Attorney", "Others"];
export const projectTypes = [
  "Special Project",
  "Constituency Project",
  "Other",
];
export const projectClassifications = [
  "Certificate Number",
  "APG Number",
  "Mobilization",
  "Retention",
  "Land and Building",
  "Plant and Machinery",
  "Furniture and Fittings",
  "Others",
];

export default () => {
  const { isMobile } = useResponsive();
  const [showPDF, setShowPDF] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [modal, setModal] = useState(false);
  const history = useNavigate();
  const [batchCode, setBatchCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [taxesLoading, setTaxesLoading] = useState(false);
  const [taxesList, setTaxesList] = useState([]);
  const [taxesApplied, setTaxesApplied] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const user = useSelector((state) => state.auth.user);
  const [mda_data, setMda_data] = useState([]);
  const totalAmount = mda_data.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0)

  const toggle = () => {
    setModal(!modal);
    if (modal === true) {
      history("/dashboard/pv_collection");
    }
  };
  
  const [pvImageInfo, setPvImageInfo] = useState([]);
  const [attachmentModalIsOpen, setAttachmentModalIsOpen] = useState(false);
  const query = useQuery();
  const imageId1 = query.get("imageId");
  const _id = query.get("id");
  const imageId = moment().format("YYYYMMDDhhmmss");
  const approval_no = query.get("approval_no");

  const [form, setForm] = useState({
    pv_code: "",
    date: moment().format("YYYY-MM-DD"),
    project_type: "",
    payment_type: "",
    mda_name: user.mda_name,
    amount: "",
    query_type: "insert",
    imageId,
  });
  const approval_type = query.get("approval_type");

  // Simplified validation function with user-friendly messages
  const validateForm = useCallback(() => {
    const newErrors = {};
    
    // Date validation with helpful message
    if (!form.date) {
      newErrors.date = "Please select the date for this payment voucher";
    }
    
    // Amount validation with clear explanation
    if (!form.amount || parseFloat(form.amount) <= 0) {
      newErrors.amount = "Please enter a valid payment amount greater than zero";
    }
    
    // Contractor details validation (not required for Recurrent)
    if (approval_type !== "Recurrent") {
      if (!form.contractor_name?.trim()) {
        newErrors.contractor_name = "Please enter the contractor's full name";
      }
      if (!form.contractor_phone?.trim()) {
        newErrors.contractor_phone = "Please provide the contractor's phone number";
      }
      if (!form.contractor_tin?.trim()) {
        newErrors.contractor_tin = "Please enter the contractor's TIN (Tax Identification Number)";
      }
      
      // Bank details validation for Capital projects
      if (approval_type === "Capital") {
        if (!form.contractor_acc_name?.trim()) {
          newErrors.contractor_acc_name = "Please enter the account holder's name as it appears on the bank account";
        }
        if (!form.contractor_acc_no?.trim()) {
          newErrors.contractor_acc_no = "Please provide the bank account number";
        }
        if (!form.contractor_bank_name?.trim()) {
          newErrors.contractor_bank_name = "Please select or enter the bank name";
        }
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [form, approval_type]);

  // Real-time validation
  useEffect(() => {
    if (submitAttempted) {
      validateForm();
    }
  }, [form, submitAttempted, validateForm]);

  // Enhanced form fields with better labels and help text
  const recurrentFormFields = useMemo(() => [
    {
      label: "Payment Date",
      value: form.date,
      name: "date",
      type: "date",
      required: true,
      error: errors.date,
      helpText: "Select the date when this payment should be processed"
    },
    {
      label: "Ministry/Department/Agency",
      value: form.mda_name,
      name: "account_description",
      disabled: true,
      helpText: "This is your organization (automatically filled)"
    },
    {
      label: "Administrative Code",
      value: form.mda_code,
      name: "mda_code",
      disabled: true,
      helpText: "Your organization's unique identifier"
    },
  ], [form.date, form.mda_name, form.mda_code, errors.date]);

  // Enhanced form fields for Capital type with better descriptions
  const capitalFormFields = useMemo(() => [
    {
      label: "Payment Date",
      value: form.date,
      name: "date",
      type: "date",
      required: true,
      error: errors.date,
      helpText: "Select the date when this payment should be processed"
    },
    {
      label: "Project Category",
      name: "project_type",
      value: form.project_type,
      col: 4,
      disabled: true,
      helpText: "Type of project (automatically determined)"
    },
    {
      label: "Payment Method",
      name: "payment_type",
      value: form.payment_type,
      col: 4,
      disabled: true,
      helpText: "How the payment will be made"
    },
    {
      label: "Payment Classification",
      value: form.payment_class,
      col: 4,
      disabled: true,
      helpText: "Category of this payment"
    },
    {
      label: "Ministry/Department/Agency",
      value: form.mda_name,
      name: "account_description",
      disabled: true,
      helpText: "Your organization (automatically filled)"
    },
    {
      label: "Administrative Code",
      value: form.mda_code,
      name: "mda_code",
      disabled: true,
      helpText: "Your organization's unique identifier"
    },
    {
      label: "Economic Classification Code",
      value: form.economic_code,
      name: "economic",
      helpText: "Budget classification for this expense"
    },
    {
      label: "Expense Description",
      value: form.description_code,
      name: "economic",
      disabled: true,
      helpText: "What this payment is for"
    },
    {
      label: "Payment Amount (₦)",
      value: formatNumber(totalAmount),
      name: "amount",
      disabled: true,
      required: true,
      error: errors.amount,
      helpText: "Total amount to be paid (from approval document)"
    },
    {
      label: "Programme Code",
      value: form.program_code,
      name: "programme",
      helpText: "Budget programme this payment belongs to"
    },
    {
      label: "Payment Description/Narration",
      value: form.narration,
      name: "programme",
      type: 'label',
      col: 6,
      helpText: "Brief description of what this payment covers"
    },
  ], [form, errors]);


  // Load taxes data with better error handling
  useEffect(() => {
    setTaxesLoading(true);
    postApi(`post_taxes`, {
      query_type: "select",
    })
      .then((data) => {
        if (data.result) {
          setTaxesList(data.result);
          // toast.success(`Loaded ${data.result.length} available tax types`);
        }
      })
      .catch((err) => {
        console.log(err);
        toast.error("Failed to load tax information. Please refresh the page.");
      })
      .finally(() => {
        setTaxesLoading(false);
      });
  }, []);

  // Load approval data with enhanced feedback
  useEffect(() => {
    if (approval_no) {
      setDataLoading(true);
      postApi(`post_approval_collection`, {
        form: {
          query_type: "select_by_approval_no_group",
          filter: "Capital",
          approval_no,
          id: approval_no,
          mda_code: user?.mda_code
        },
      })
        .then((data) => {
          if (data && data.result && data.result.length) {
            setMda_data(data.result);
            setForm((p) => ({ 
              ...p, 
              ...data.result[0], 
              mda_name: data.result[0].mda_name && data.result[0].mda_name !== "" 
                ? data.result[0].mda_name 
                : user.mda_name
            }));
            // toast.success("Approval information loaded successfully");
          } else {
            toast.warning("No approval data found for this approval number");
          }
        })
        .catch((err) => {
          console.log(err);
          toast.error("Failed to load approval information. Please check the approval number.");
        })
        .finally(() => {
          setDataLoading(false);
        });
    }
  }, [approval_no, user?.mda_code, user.mda_name]);

  // Simplified form change handler
  const handleChange = useCallback(({ target: { name, value } }) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  }, [errors]);

  // File upload and attachment handling
  const {
    acceptedFiles,
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({ accept: "image/*, .pdf, text/plain" });

  const attachmentModalToggle = () => {
    setAttachmentModalIsOpen((p) => !p);
  };

  const viewAttachment = () => {
    fetchApi(`fetch-pv-images?pv_no=${imageId1}`, (resp) => {
      attachmentModalToggle();
      if (resp.results && resp.results.length) {
        setPvImageInfo(resp.results);
      } else {
        setPvImageInfo({});
      }
    });
  };

  const handleApi1 = (pv_no) => {
    // console.log(form)

    // if (form.approved_amount && form.approved_amount !== '') {
    const formData = new FormData();
    form.pv_no = pv_no;
    for (let i = 0; i < acceptedFiles.length; i++) {
      formData.append("files", acceptedFiles[i]);
    }

    if (!acceptedFiles.length) {
      setForm((p) => ({ ...p, approval: "not approved" }));
      formData.append("form", JSON.stringify(form));
      // // formData.append('event_name', options[0].event_name)

      fetch(`${apiURL}/post_images_pv`, {
        method: "POST",

        body: formData,
      })
        .then((response) => response.json())
        .then((result) => {
          // handleApi()
          // setLoadSpinner(false)
          console.log(result);
          // removeAll()
          // toggle()
          // let acceptedFiles = []

          // localStorage.setItem("key", JSON.stringify(result.token));
          // setName(result.user)
          // history("/homepage/payment_table");

          // console.log(result);
        })

        .catch((err) => {
          console.log(err);
          // alert(err);
          // setLoadSpinner(false)
        });
    } else {
      formData.append("form", JSON.stringify(form));
      // // formData.append('event_name', options[0].event_name)

      fetch(`${apiURL}/post_images_pv`, {
        method: "POST",

        body: formData,
      })
        .then((response) => response.json())
        .then((result) => {
          // handleApi()
          // setLoadSpinner(false)
          console.log(result);
          // removeAll()
          // toggle()
          // let acceptedFiles = []

          // localStorage.setItem("key", JSON.stringify(result.token));
          // setName(result.user)
          // history("/homepage/payment_table");

          // console.log(result);
        })

        .catch((err) => {
          console.log(err);
          // alert(err);
          // setLoadSpinner(false)
        });
    }
    // Object.keys(form).forEach(item => {
    //   formData.append(item, form[item])
    // })

    // formData.append('user', name.email)
    //  formData.append('group_id', groupName)
    // } else {
    //   alert('Please enter a valid amount')
    // }
  };

  // Enhanced API submission with better user feedback and error handling
  const handleApi = useCallback((e) => {
    // Prevent default form submission
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    setSubmitAttempted(true);
    
    if (!validateForm()) {
      // Count the number of errors and provide helpful feedback
      const errorFields = Object.keys(errors);
      const errorCount = errorFields.length;
      
      toast.error(
        `Please complete ${errorCount} required field${errorCount > 1 ? 's' : ''} before submitting`, 
        { duration: 4000 }
      );
      
      // Scroll to first error field with smooth animation
      const firstErrorField = document.querySelector('.is-invalid');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => firstErrorField.focus(), 500);
      }
      return;
    }

    setLoading(true);
    
    // Show progress feedback to user
    const isCapitalProject = approval_type === "Capital";
    const progressMessage = isCapitalProject 
      ? "Creating capital project payment vouchers..." 
      : "Creating payment voucher...";
    
    toast.loading(progressMessage, { id: 'pv-creation' });
    
    // Helper function to update budget code
    const updateBudgetCode = (description, query_type) => {
      return postApi(`update-budget-code`, { description, query_type })
        .then((d) => {
          if (d.result) {
            setBatchCode(d.pv_code);
          }
        })
        .catch((err) => console.log(err));
    };

    if (isCapitalProject) {
      // Enhanced capital project creation with detailed feedback
      postApi(`create_pv`, {
        approval_type,
        approval_no,
        form: {
          ...form,
          approval_no,
          approval_type,
          userId: user.username,
        },
        list: mda_data.map((d) => ({
          approval_no,
          userId: user.username,
          ...form,
          ...d,
          account_name: form.mda_account_name,
          bank_name: form.mda_account_bank_name,
          account_type: form.mda_account_type,
          sort_code: form.mda_account_sort_code,
          account_number: form.mda_acc_no || form.mda_account_no,
          sub: form.sub,
          sub_sub: form.sub_sub,
          sub_sub_sub: form.sub_sub_sub,
          payment_to: form.payment_to,
        })),
        taxesApplied,
        notif_key: "Check PV",
      })
        .then((data) => {
          if (data.success) {
            handleApi1(data.pv_code);
            updateBudgetCode("pv_code", "update");
            
            // Calculate and display totals for user confirmation
            const totalTaxAmount = taxesApplied.reduce((sum, tax) => sum + parseFloat(tax.amount || 0), 0);
            const netAmount = parseFloat(totalAmount || 0) - totalTaxAmount;
            
            toast.success(
              `✅ Capital Project PVs Created Successfully!\n` +
              `Main PV Code: ${data.pv_code}\n` +
              `Total PVs Created: ${data.total_pvs || (1 + taxesApplied.length)}\n` +
              `Gross Amount: ₦${parseFloat(totalAmount || 0).toLocaleString()}\n` +
              `Tax Deductions: ₦${totalTaxAmount.toLocaleString()}\n` +
              `Net Amount: ₦${netAmount.toLocaleString()}`,
              { id: 'pv-creation', duration: 8000 }
            );
            
            setBatchCode(data.pv_code);
            toggle();

            // Navigate to view the created PV
            history(
              `${Routes.cashBacking.NewViewPVPDf}?approval_no=${encodeURIComponent(approval_no)}&approval_type=${encodeURIComponent(approval_type)}&pv_code=${encodeURIComponent(data.pv_code)}`
            );
          } else {
            toast.error(data.message || data.error || "Failed to create capital project PVs. Please try again.", { id: 'pv-creation' });
          }
        })
        .catch((error) => {
          console.error(error);
          toast.error("Something went wrong while creating the PV. Please check your connection and try again.", { id: 'pv-creation' });
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // Enhanced standard PV creation
      postApi(`post_pv_collection`, {
        form: {
          ...form,
          approval_no,
          userId: user.username,
        },
        list: mda_data.map((d) => ({
          approval_no,
          userId: user.username,
          ...form,
          ...d,
          account_name: form.mda_account_name,
          bank_name: form.mda_account_bank_name,
          account_type: form.mda_account_type,
          sort_code: form.mda_account_sort_code,
          account_number: form.mda_acc_no || form.mda_account_no,
          sub: form.sub,
          sub_sub: form.sub_sub,
          sub_sub_sub: form.sub_sub_sub,
          payment_to: form.payment_to,
        })),
        taxesApplied,
        notif_key: "Check PV",
      })
        .then((data) => {
          if (data.success) {
            handleApi1(data.approval_no);
            updateBudgetCode("pv_code", "update");
            
            toast.success(
              `✅ Payment Voucher Created Successfully!\n` +
              `PV Code: ${data.pv_code}\n` +
              `Amount: ₦${parseFloat(totalAmount || 0).toLocaleString()}`,
              { id: 'pv-creation', duration: 5000 }
            );
            
            setBatchCode(data.pv_code);
            toggle();

            history(
              `${Routes.cashBacking.NewViewPVPDf}?approval_no=${encodeURIComponent(approval_no)}&approval_type=${encodeURIComponent(approval_type)}`
            );
          } else {
            toast.error(data.message || "Failed to create payment voucher. Please try again.", { id: 'pv-creation' });
          }
        })
        .catch((error) => {
          console.error(error);
          toast.error("Something went wrong while creating the PV. Please check your connection and try again.", { id: 'pv-creation' });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [form, approval_no, user.username, mda_data, taxesApplied, approval_type, validateForm, errors, history]);

  // Preview function - validates form and shows preview without submitting
  const handlePreview = useCallback((e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    setSubmitAttempted(true);
    
    if (!validateForm()) {
      const errorFields = Object.keys(errors);
      const errorCount = errorFields.length;
      
      toast.error(
        `Please complete ${errorCount} required field${errorCount > 1 ? 's' : ''} before previewing`, 
        { duration: 4000 }
      );
      
      const firstErrorField = document.querySelector('.is-invalid');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => firstErrorField.focus(), 500);
      }
      return;
    }

    // Show preview
    setShowPreview(true);
    toast.success("Preview generated! Review your PV before final submission.", { duration: 3000 });
  }, [validateForm, errors]);

  // Function to go back to edit mode from preview
  const handleBackToEdit = useCallback(() => {
    setShowPreview(false);
  }, []);

  return (
    <ResponsiveContainer>
      <BackButton />
      
      {/* Simple Header */}
      <Card className={`mb-3 ${isMobile ? 'card-mobile' : ''}`}>
        <Card.Body className="py-3">
          <Row className="align-items-center">
            <Col xs={12} md={8} className={isMobile ? 'mb-2' : ''}>
              <h4 className="mb-1">Create Payment Voucher</h4>
              <div className="text-muted">
                <small>Approval No: {approval_no} | Type: {approval_type}</small>
              </div>
            </Col>
            <Col xs={12} md={4} className={isMobile ? 'text-start' : 'text-end'}>
              <ResponsiveButton
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowHelp(!showHelp)}
                className="me-2"
                mobileFullWidth={false}
              >
                <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                {showHelp ? 'Hide' : 'Show'} Help
              </ResponsiveButton>
              
              <ResponsiveButton
                variant="outline-primary"
                size="sm"
                onClick={() =>
                  history(
                    `${Routes.cashBacking.releaseCollectionPDF}?approval_no=${encodeURIComponent(approval_no)}`
                  )
                }
              >
                <FontAwesomeIcon icon={faEye} className="me-1" />
                View Approval
              </ResponsiveButton>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Help Section */}
      {showHelp && (
        <Alert variant="light" className="mb-4 border">
          <h6><FontAwesomeIcon icon={faLightbulb} className="me-2" />What you need to do:</h6>
          <ol className="mb-0">
            {approval_type !== "Recurrent" && <li>Enter contractor information (name, phone, TIN)</li>}
            {approval_type === "Capital" && <li>Provide bank account details for payment</li>}
            {approval_type !== "Recurrent" && <li>Review and apply any applicable taxes</li>}
            <li>Attach supporting documents if required</li>
            <li>Review everything and submit</li>
          </ol>
        </Alert>
      )}

      {/* Main Form Card */}
      <Card>
        <Card.Body>
          {dataLoading && (
            <Alert variant="info" className="d-flex align-items-center mb-4">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading approval information...
            </Alert>
          )}

          {showPDF ? (
            <Trailbalance
              form={form}
              user={user}
              data={mda_data}
              taxesList={taxesList}
              taxesApplied={taxesApplied}
            />
          ) : showPreview ? (
            // PREVIEW MODE
            <div className="preview-fade-in">
              {/* Preview Header */}
              <div className="preview-header">
                <Row className="align-items-center">
                  <Col xs={12} md={8}>
                    <h5 className="mb-1">
                      <FontAwesomeIcon icon={faEye} className="me-2" />
                      Payment Voucher Preview
                    </h5>
                    <p className="mb-0">
                      This is how your PV will appear after submission. Review carefully before final submission.
                    </p>
                  </Col>
                  <Col xs={12} md={4} className={`${isMobile ? 'mt-2 text-start' : 'text-end'}`}>
                    <ResponsiveButton
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleBackToEdit}
                      className="me-2"
                    >
                      <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
                      Back to Edit
                    </ResponsiveButton>
                    {/* <ResponsiveButton
                      variant="outline-primary"
                      size="sm"
                      onClick={() => window.print()}
                    >
                      <FontAwesomeIcon icon={faPrint} className="me-1" />
                      Print Preview
                    </ResponsiveButton> */}
                  </Col>
                </Row>
              </div>

              {/* Preview Content */}
              <div className="preview-container">
                <Trailbalance
                  form={form}
                  user={user}
                  data={mda_data}
                  taxesList={taxesList}
                  taxesApplied={taxesApplied}
                  isPreview={true}
                  isMultiplePVs={taxesApplied.length > 0}
                  showTaxSummary={taxesApplied.length > 0}
                />
              </div>

              {/* Preview Actions */}
              <div className="preview-actions text-center">
                {/* Multiple PVs Information */}
                {taxesApplied.length > 0 && (
                  <div className="mb-4">
                    <Alert variant="info" className="d-inline-block text-start">
                      <h6 className="mb-3">
                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                        Multiple Payment Vouchers Will Be Generated
                      </h6>
                      <div className="mb-3">
                        <strong>📋 Main PV (Net Payment):</strong>
                        <ul className="mb-2 mt-1">
                          <li>Contains complete payment details and tax breakdown</li>
                          <li>Amount: ₦{formatNumber(parseFloat(form?.amount || 0) - taxesApplied.reduce((sum, tax) => sum + parseFloat(tax.amount || 0), 0))} (Net after taxes)</li>
                          <li>Shows detailed narration with tax summary</li>
                        </ul>
                        
                        <strong>💰 Tax Deduction PVs ({taxesApplied.length}):</strong>
                        <ul className="mb-2 mt-1">
                          {taxesApplied.map((tax, index) => (
                            <li key={index}>
                              {tax.description}: ₦{formatNumber(tax.amount)}
                              {tax.rate && ` (${tax.rate}%)`}
                            </li>
                          ))}
                        </ul>
                        
                        <div className="bg-light p-2 rounded mt-2">
                          <strong>📊 Summary:</strong><br/>
                          Gross Amount: ₦{formatNumber(form?.amount || 0)}<br/>
                          Total Taxes: ₦{formatNumber(taxesApplied.reduce((sum, tax) => sum + parseFloat(tax.amount || 0), 0))}<br/>
                          Net Payment: ₦{formatNumber(parseFloat(form?.amount || 0) - taxesApplied.reduce((sum, tax) => sum + parseFloat(tax.amount || 0), 0))}<br/>
                          <strong>Total PVs: {1 + taxesApplied.length}</strong>
                        </div>
                      </div>
                    </Alert>
                  </div>
                )}
                
                <div className="d-flex justify-content-center gap-3 flex-wrap">
                  <ResponsiveButton
                    variant="outline-secondary"
                    onClick={handleBackToEdit}
                    className="px-4"
                  >
                    <FontAwesomeIcon icon={faEdit} className="me-2" />
                    Make Changes
                  </ResponsiveButton>
                  
                  <ResponsiveButton
                    variant="success"
                    onClick={handleApi}
                    disabled={loading}
                    className="px-4"
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Creating {taxesApplied.length > 0 ? `${1 + taxesApplied.length} PVs` : 'PV'}...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                        Confirm & Submit {taxesApplied.length > 0 ? `${1 + taxesApplied.length} PVs` : 'PV'}
                      </>
                    )}
                  </ResponsiveButton>
                </div>
                
                <Alert variant="warning" className="d-inline-block mt-3">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                  <strong>Final Check:</strong> Once submitted, {taxesApplied.length > 0 ? 'these PVs' : 'this PV'} will be sent for approval and cannot be easily modified.
                  {taxesApplied.length > 0 && (
                    <div className="mt-2">
                      <small>All {1 + taxesApplied.length} PVs will be linked together and processed as a group.</small>
                    </div>
                  )}
                </Alert>
              </div>
            </div>
          ) : (
            <>
              <Form onSubmit={handleApi} noValidate>
                {/* Error Summary */}
                {submitAttempted && Object.keys(errors).length > 0 && (
                  <Alert variant="danger" className="mb-4">
                    <h6><FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />Please complete the following:</h6>
                    <ul className="mb-0">
                      {Object.entries(errors).map(([field, error]) => (
                        <li key={field}>{error}</li>
                      ))}
                    </ul>
                  </Alert>
                )}
                
                {/* Basic Information - Read Only */}
                <div className="mb-4">
                  <h6 className="text-muted mb-3">Payment Information (Read Only)</h6>
                  <div className="bg-light p-3 rounded">
                    {approval_type === "Recurrent" ? (
                      <CustomForm
                        handleChange={handleChange}
                        fields={recurrentFormFields}
                      />
                    ) : (
                      <CustomForm
                        handleChange={handleChange}
                        fields={capitalFormFields}
                      />
                    )}
                  </div>
                </div>
                
                {/* Budget Breakdown - Read Only for Recurrent */}
                {approval_type === "Recurrent" && mda_data.length > 0 && (
                  <div className="mb-4">
                    <h6 className="text-muted mb-3">Budget Breakdown (Read Only)</h6>
                    <div className="bg-light p-3 rounded">
                      <Table responsive className="mb-0">
                        <thead>
                          <tr>
                            <th>Description</th>
                            <th>Economic Code</th>
                            <th className="text-end">Amount (₦)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {mda_data.map((item, index) => (
                            <tr key={index}>
                              <td>{item.description_code}</td>
                              <td><small className="text-muted">{item.economic_code}</small></td>
                              <td className="text-end">{formatNumber(item.amount)}</td>
                            </tr>
                          ))}
                          <tr className="fw-bold">
                            <td colSpan="2">Total Amount</td>
                            <td className="text-end">
                              ₦{formatNumber(totalAmount)}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* ACTION SECTIONS START HERE */}
                
                {/* Document Attachment Section */}
                {!_id && (
                  <div className="mb-4">
                    <h6 className="mb-3">
                      <FontAwesomeIcon icon={faFileInvoice} className="me-2" />
                      Supporting Documents <small className="text-muted">(Optional)</small>
                    </h6>
                    <div className="border rounded p-3">
                      <Attachment
                        acceptedFiles={acceptedFiles}
                        getRootProps={getRootProps}
                        getInputProps={getInputProps}
                        isDragActive={isDragActive}
                        isDragAccept={isDragAccept}
                        isDragReject={isDragReject}
                      />
                    </div>
                  </div>
                )}
                
                {/* View Existing Attachment */}
                {_id && (
                  <div className="mb-4 text-center">
                    <ResponsiveButton variant="outline-secondary" onClick={viewAttachment}>
                      <FontAwesomeIcon icon={faEye} className="me-2" />
                      View Existing Attachments
                    </ResponsiveButton>
                  </div>
                )}

                {/* Contractor/Organization Information */}
                {approval_type === "Recurrent" ? (
                  <div className="mb-4">
                    <h6 className="mb-3">
                      <FontAwesomeIcon icon={faUniversity} className="me-2" />
                      Organization Bank Details <span className="text-danger">*</span>
                    </h6>
                    <div className="border rounded p-3">
                      <NewPVBankDetails 
                        form={form} 
                        setForm={setForm}
                        errors={errors}
                        setErrors={setErrors}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Contractor Information */}
                    <div className="mb-4">
                      <h6 className="mb-3">
                        <FontAwesomeIcon icon={faUser} className="me-2" />
                        Contractor Information <span className="text-danger">*</span>
                      </h6>
                      <div className="border rounded p-3">
                        <NewContractorForm 
                          form={form} 
                          setForm={setForm}
                          errors={errors}
                          setErrors={setErrors}
                        />
                      </div>
                    </div>

                    {/* Tax Calculations */}
                    {approval_type === "Capital" ? (
                      <div className="mb-4">
                        <h6 className="mb-3">
                          <FontAwesomeIcon icon={faCalculator} className="me-2" />
                          Tax Calculations
                        </h6>
                        <div className="border rounded p-3">
                          <CapitalProjectTaxSection
                            form={form}
                            taxesList={taxesList}
                            taxesApplied={taxesApplied}
                            setTaxesApplied={setTaxesApplied}
                            taxesLoading={taxesLoading}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="mb-4">
                        <h6 className="mb-3">
                          <FontAwesomeIcon icon={faCalculator} className="me-2" />
                          Tax Calculations
                        </h6>
                        <div className="border rounded p-3">
                          {taxesLoading ? (
                            <div className="text-center py-3">
                              <Spinner animation="border" size="sm" className="me-2" />
                              Loading available taxes...
                            </div>
                          ) : (
                            <>
                              <p className="text-muted mb-3">
                                Select applicable taxes. Tax amounts will be automatically calculated.
                              </p>
                              <Row>
                                {taxesList.map((item, index) => (
                                  <TaxItem
                                    key={index}
                                    index={index}
                                    item={item}
                                    form={form}
                                    taxesApplied={taxesApplied}
                                    setTaxesApplied={setTaxesApplied}
                                  />
                                ))}
                              </Row>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Payment Summary */}
                {form.amount && (
                  <div className="mb-4">
                    <h6 className="mb-3">Payment Summary</h6>
                    <div className="bg-light p-3 rounded">
                      <Row className="text-center">
                        <Col md={4}>
                          <div className="mb-2">
                            <strong>Gross Amount</strong>
                            <div className="h5 mb-0">₦{formatNumber(totalAmount)}</div>
                          </div>
                        </Col>
                        {taxesApplied.length > 0 && (
                          <Col md={4}>
                            <div className="mb-2">
                              <strong>Tax Deductions</strong>
                              <div className="h5 mb-0 text-warning">
                                -₦{formatNumber(taxesApplied.reduce((sum, tax) => sum + parseFloat(tax.amount || 0), 0))}
                              </div>
                            </div>
                          </Col>
                        )}
                        <Col md={taxesApplied.length > 0 ? 4 : 8}>
                          <div className="mb-2">
                            <strong>Net Payment</strong>
                            <div className="h5 mb-0 text-success">
                              ₦{formatNumber(
                                totalAmount - 
                                taxesApplied.reduce((sum, tax) => sum + parseFloat(tax.amount || 0), 0)
                              )}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </div>
                )}
                
                {/* Submit Buttons */}
                <div className="text-center">
                  <div className="d-flex justify-content-center gap-3 flex-wrap">
                    {/* Preview Button */}
                    <ResponsiveButton
                      variant="outline-primary"
                      onClick={handlePreview}
                      disabled={loading || dataLoading}
                      className="px-4"
                    >
                      <FontAwesomeIcon icon={faEye} className="me-2" />
                      Preview PV
                    </ResponsiveButton>
                    
                    {/* Direct Submit Button */}
                    <ResponsiveButton
                      type="submit"
                      variant="success"
                      disabled={loading || dataLoading}
                      className="px-4"
                    >
                      {loading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Creating PV...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                          Create & Submit PV
                        </>
                      )}
                    </ResponsiveButton>
                  </div>
                  
                  <div className="mt-3">
                    <small className="text-muted d-block">
                      <FontAwesomeIcon icon={faLightbulb} className="me-1 text-warning" />
                      <strong>Tip:</strong> Use "Preview PV" to see exactly how your payment voucher will look before submitting.
                    </small>
                    <small className="text-muted">
                      Or click "Create & Submit PV" to create the voucher directly.
                    </small>
                  </div>
                </div>
              </Form>
              
              <ViewImage
                attachmentModalIsOpen={attachmentModalIsOpen}
                attachmentModalToggle={attachmentModalToggle}
                pvImageInfo={pvImageInfo}
                imageExtensions={imageExtensions}
              />
            </>
          )}
        </Card.Body>
      </Card>
    </ResponsiveContainer>
  );
};

export const ViewImage = ({
  attachmentModalIsOpen = false,
  attachmentModalToggle = (f) => f,
  pvImageInfo = [],
  imageExtensions = [],
}) => {
  return (
    <Modal show={attachmentModalIsOpen} onHide={attachmentModalToggle}>
      <Modal.Header show={attachmentModalIsOpen} onHide={attachmentModalToggle}>
        PV No.: {pvImageInfo.pv_no}
      </Modal.Header>
      <Modal.Body className="text-center">
        <Container>
          <div className="image-container">
            {pvImageInfo.length ? (
              pvImageInfo.map((item, index) =>
                imageExtensions.some((extension) =>
                  item.image_url.toLowerCase().endsWith(extension)
                ) ? (
                  <Card.Img
                    top
                    src={`${apiURL}/uploads/${item.image_url}`}
                    alt={"pv"}
                  />
                ) : (
                  <Card.Body>
                    <Card.Link
                      href={`${apiURL}/uploads/${item.image_url}`}
                      target="_blank"
                    >
                      View PDF
                    </Card.Link>
                  </Card.Body>
                )
              )
            ) : (
              <h4>No attachment found.</h4>
            )}
          </div>
        </Container>
      </Modal.Body>
    </Modal>
  );
};
